import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, TableRow, TableCell } from '@mui/material';
import { useAppSelector } from '../../../store';
import {
  Order,
  OrderBundleComponent,
  OrderBundleItem,
} from '../../../store/order/api/interface';
import { resolveAssetUrl } from '../../../utils/url';
import {
  bundleUsesOverridePricing,
  formatBundleRowPrice,
  getBundleLineTotal,
} from '../../../utils/orderTotals';
import { buildBundleComponentMergedImageMap } from '../../../utils/orderMergedImages';

interface IProps {
  order: Order;
  bundleItem?: OrderBundleItem;
  lineSubtotal?: number;
  hideBundleHeader?: boolean;
  hideLineSubtotal?: boolean;
  includeCakeComponents?: boolean;
  handleOpenLightBox?: (imageObject: string) => void;
}

const getBundleHeaderPrice = (bundleItem: OrderBundleItem) => {
  const nonCakeComponents =
    bundleItem.components?.filter(
      (component) => component.componentType !== 'cake'
    ) ?? [];
  const hasChildren =
    nonCakeComponents.length > 0 ||
    (bundleItem.variantSelections?.length ?? 0) > 0;

  if (!hasChildren) {
    return getBundleLineTotal(bundleItem);
  }

  if (bundleUsesOverridePricing(bundleItem)) {
    return getBundleLineTotal(bundleItem);
  }

  return '-';
};

const OrderBundleExtrasCollapseRows: React.FC<IProps> = ({
  order,
  bundleItem,
  lineSubtotal,
  hideBundleHeader = false,
  hideLineSubtotal = false,
  includeCakeComponents = false,
  handleOpenLightBox,
}) => {
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );
  const [cakeImageMap, setCakeImageMap] = useState<Record<string, string | null>>(
    {}
  );
  const [cakeImagesLoading, setCakeImagesLoading] = useState(false);

  const bundleItems = useMemo(
    () =>
      bundleItem ? [bundleItem] : order?.order_bundle_items ?? [],
    [bundleItem, order?.order_bundle_items]
  );

  const cakeComponentIds = useMemo(
    () =>
      bundleItems
        .flatMap((item) => item.components ?? [])
        .filter((component) => component.componentType === 'cake')
        .map((component) => component.id)
        .join('|'),
    [bundleItems]
  );

  useEffect(() => {
    let cancelled = false;

    const loadCakeImages = async () => {
      if (!cakeComponentIds) {
        setCakeImageMap({});
        setCakeImagesLoading(false);
        return;
      }

      setCakeImagesLoading(true);
      const imageMap = await buildBundleComponentMergedImageMap(order, assetUrl);
      if (!cancelled) {
        setCakeImageMap(imageMap);
        setCakeImagesLoading(false);
      }
    };

    loadCakeImages();

    return () => {
      cancelled = true;
    };
  }, [order, assetUrl, cakeComponentIds]);

  if (!bundleItem && !order?.order_bundle_items?.length) {
    return null;
  }

  const renderPreviewImage = (imageSrc?: string | null, lightboxSrc?: string) => {
    if (!imageSrc) {
      return '-';
    }

    return (
      <Box
        component='img'
        src={imageSrc}
        alt=''
        onClick={
          handleOpenLightBox && lightboxSrc
            ? () => handleOpenLightBox(lightboxSrc)
            : undefined
        }
        sx={{
          width: 48,
          height: 48,
          m: 1,
          objectFit: 'contain',
          cursor: handleOpenLightBox ? 'pointer' : 'default',
          display: 'block',
        }}
      />
    );
  };

  const renderVariantImage = (previewUrl?: string | null) => {
    if (!previewUrl?.trim()) {
      return '-';
    }

    const imageSrc = resolveAssetUrl(previewUrl, assetUrl);
    return renderPreviewImage(imageSrc, previewUrl);
  };

  const renderComponentImage = (component: OrderBundleComponent) => {
    if (component.componentType === 'cake') {
      const mergedImage = cakeImageMap[component.id];
      if (mergedImage) {
        return renderPreviewImage(mergedImage, mergedImage);
      }

      if (cakeImagesLoading && component.category_cake) {
        return <CircularProgress size={20} />;
      }

      const fallbackUrl =
        component.category_cake?.flavor?.previewUrl ||
        component.category_cake?.shape?.previewUrl ||
        component.previewUrl;

      return renderVariantImage(fallbackUrl);
    }

    return renderVariantImage(component.previewUrl);
  };

  return (
    <>
      {bundleItems.map((item) => {
        const showChildPrices = !bundleUsesOverridePricing(item);
        const visibleComponents = includeCakeComponents
          ? item.components ?? []
          : item.components?.filter(
              (component) => component.componentType !== 'cake'
            ) ?? [];

        return (
          <React.Fragment key={item.id}>
            {!hideBundleHeader && (
              <TableRow>
                <TableCell align='center'>Bundle Extra</TableCell>
                <TableCell align='center'>
                  {item.labelEn}
                  {item.labelAr ? ` (${item.labelAr})` : ''}
                </TableCell>
                <TableCell align='center'>-</TableCell>
                <TableCell align='center'>
                  {getBundleHeaderPrice(item)}
                </TableCell>
              </TableRow>
            )}
            {visibleComponents.map((component) => (
              <TableRow key={component.id}>
                <TableCell align='center' sx={{ pl: 4 }}>
                  {component.componentType}
                </TableCell>
                <TableCell align='center'>
                  {component.labelEn}
                  {component.labelAr ? ` (${component.labelAr})` : ''}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ display: 'flex', justifyContent: 'center' }}
                >
                  {renderComponentImage(component)}
                </TableCell>
                <TableCell align='center'>
                  {showChildPrices
                    ? formatBundleRowPrice(component.subTotal, component.unitPrice)
                    : '-'}
                </TableCell>
              </TableRow>
            ))}
            {item.variantSelections?.map((selection) => (
              <TableRow key={selection.id}>
                <TableCell align='center' sx={{ pl: 4 }}>
                  {selection.variantGroupLabelEn || 'Variant'}
                </TableCell>
                <TableCell align='center'>
                  {selection.labelEn}
                  {selection.labelAr ? ` (${selection.labelAr})` : ''}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ display: 'flex', justifyContent: 'center' }}
                >
                  {renderVariantImage(selection.previewUrl)}
                </TableCell>
                <TableCell align='center'>
                  {showChildPrices
                    ? formatBundleRowPrice(selection.subTotal, selection.price)
                    : '-'}
                </TableCell>
              </TableRow>
            ))}
            {showChildPrices &&
              (visibleComponents.length > 0 ||
                (item.variantSelections?.length ?? 0) > 0) && (
                <TableRow>
                  <TableCell align='center' sx={{ pl: 2, fontWeight: 600 }}>
                    Bundle Total
                  </TableCell>
                  <TableCell align='center' colSpan={2} />
                  <TableCell align='center' sx={{ fontWeight: 600 }}>
                    {getBundleLineTotal(item)}
                  </TableCell>
                </TableRow>
              )}
          </React.Fragment>
        );
      })}
      {!hideLineSubtotal && lineSubtotal !== undefined && (
        <TableRow>
          <TableCell align='center' colSpan={3} sx={{ fontWeight: 600 }}>
            Subtotal
          </TableCell>
          <TableCell align='center' sx={{ fontWeight: 600 }}>
            {lineSubtotal}
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default OrderBundleExtrasCollapseRows;
